import { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { MapPin, ArrowLeft, Camera, X } from 'lucide-react';

const Auth = () => {
  const { login, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialToken, setTutorialToken] = useState(null);
  const [tutorialUser, setTutorialUser] = useState(null);
  const videoRef = useRef(null);

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // Register state
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerUsername, setRegisterUsername] = useState('');
  const [registerGender, setRegisterGender] = useState('');
  const [registerAge, setRegisterAge] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [emergencyFirstName, setEmergencyFirstName] = useState('');
  const [emergencyLastName, setEmergencyLastName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [emergencyRelationship, setEmergencyRelationship] = useState('');
  const [emergencyEmail, setEmergencyEmail] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePictureFile, setProfilePictureFile] = useState(null);

  // Check for pending profile picture from Landing page
  useEffect(() => {
    const pendingPic = sessionStorage.getItem('pendingProfilePic');
    if (pendingPic) {
      setProfilePicture(pendingPic);
      // Convert base64 to file
      fetch(pendingPic)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
          setProfilePictureFile(file);
        });
      sessionStorage.removeItem('pendingProfilePic');
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePictureFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicture(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePicture = () => {
    setProfilePicture(null);
    setProfilePictureFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadProfilePicture = async (token) => {
    if (!profilePictureFile) return;
    
    try {
      const formData = new FormData();
      formData.append('picture', profilePictureFile);
      
      const response = await axios.post(`${API}/auth/upload-profile-picture`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast.success('Profile picture saved!');
      return response.data;
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      toast.error('Profile created but picture upload failed. You can add it later in your profile.');
      return null;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginEmail,
        password: loginPassword,
        remember_me: rememberMe
      });
      login(response.data.token, response.data.user);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/google-callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerGender) {
      toast.error('Please select your gender');
      return;
    }
    if (!registerAge || registerAge < 21) {
      toast.error('You must be at least 21 years old');
      return;
    }
    if (!emergencyFirstName || !emergencyLastName || !emergencyPhone || !emergencyRelationship) {
      toast.error('All emergency contact fields are required');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: registerEmail,
        password: registerPassword,
        username: registerUsername,
        gender: registerGender,
        age: parseInt(registerAge),
        date_of_birth: dateOfBirth || null,
        emergency_contact_first_name: emergencyFirstName,
        emergency_contact_last_name: emergencyLastName,
        emergency_contact_phone: emergencyPhone,
        emergency_contact_relationship: emergencyRelationship,
        emergency_contact_email: emergencyEmail || null
      });
      
      const token = response.data.token;
      const userData = response.data.user;
      
      // Upload profile picture BEFORE login (login triggers redirect)
      if (profilePictureFile) {
        await uploadProfilePicture(token);
      }
      
      // Show tutorial video before completing login
      setTutorialToken(token);
      setTutorialUser(userData);
      setShowTutorial(true);
      toast.success('Account created! Watch a quick tutorial to get started.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const completeTutorial = () => {
    if (tutorialToken && tutorialUser) {
      login(tutorialToken, tutorialUser);
    }
    setShowTutorial(false);
  };

  // Tutorial Modal
  if (showTutorial) {
    return (
      <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" data-testid="tutorial-modal">
        <div className="w-full max-w-3xl">
          <div className="text-center mb-6">
            <img src="/nightbuzz-logo.png" alt="NightBuZz" className="h-16 mx-auto mb-3" />
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Welcome to NightBuZz!
            </h2>
            <p className="text-slate-400 text-sm mt-1">Watch this quick tutorial to get the most out of the app</p>
          </div>
          <div className="rounded-xl overflow-hidden border border-slate-700/50 shadow-2xl mb-6">
            <video
              ref={videoRef}
              controls
              autoPlay
              className="w-full"
              data-testid="tutorial-modal-video"
              onEnded={completeTutorial}
            >
              <source src="/nightbuzz-tutorial.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="flex justify-center">
            <Button
              onClick={completeTutorial}
              variant="ghost"
              className="text-slate-500 hover:text-white hover:bg-slate-800 text-sm"
              data-testid="skip-tutorial-btn"
            >
              Skip Tutorial
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md glass border border-slate-700 p-8">
        <div className="text-center mb-8 fade-in">
          <div className="flex flex-col items-center justify-center gap-4 mb-4">
            <img 
              src="/nightbuzz-logo.png" 
              alt="NightBuZz" 
              className="h-56 md:h-64"
              data-testid="auth-logo"
            />
          </div>
          <p className="text-slate-300">Find the hottest bars in your area</p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8 bg-slate-800">
            <TabsTrigger value="login" className="data-[state=active]:bg-purple-600" data-testid="login-tab">
              Login
            </TabsTrigger>
            <TabsTrigger value="register" className="data-[state=active]:bg-purple-600" data-testid="register-tab">
              Register
            </TabsTrigger>
          </TabsList>
          <p className="text-center text-sm text-amber-400/90 mb-4 font-medium">BuZZers (bar customers)</p>

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-email" className="text-white">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="your@email.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="login-password" className="text-white">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  placeholder="••••••••"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="login-password-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-purple-600 focus:ring-purple-500"
                  data-testid="remember-me-checkbox"
                />
                <Label htmlFor="remember-me" className="text-slate-300 text-sm cursor-pointer">Remember me for 30 days</Label>
              </div>
              <Button
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-700"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-slate-900 px-3 text-slate-500">or continue with</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full border-slate-700 text-white hover:bg-slate-800 flex items-center justify-center gap-3"
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full border-slate-700 text-white hover:bg-slate-800 flex items-center justify-center gap-3"
                disabled
                data-testid="apple-login-btn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Continue with Apple (Coming Soon)
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-4">
              {/* Profile Picture Upload */}
              <div className="flex justify-center mb-4">
                <div 
                  className="relative w-24 h-24 cursor-pointer group"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className={`w-full h-full rounded-full border-4 ${
                    profilePicture 
                      ? 'border-purple-500' 
                      : 'border-dashed border-purple-500/50 hover:border-purple-400'
                  } transition-all flex items-center justify-center overflow-hidden bg-purple-600/20`}>
                    {profilePicture ? (
                      <img src={profilePicture} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center">
                        <Camera className="w-8 h-8 text-purple-400 mx-auto" />
                        <span className="text-xs text-purple-400">Add Photo</span>
                      </div>
                    )}
                  </div>
                  {profilePicture && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemovePicture();
                      }}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </div>
              <p className="text-center text-xs text-slate-400 -mt-2 mb-4">Click to add a profile photo</p>
              
              <div>
                <Label htmlFor="register-username" className="text-white">Username</Label>
                <Input
                  id="register-username"
                  type="text"
                  placeholder="johndoe"
                  value={registerUsername}
                  onChange={(e) => setRegisterUsername(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="register-username-input"
                />
              </div>
              <div>
                <Label htmlFor="register-email" className="text-white">Email</Label>
                <Input
                  id="register-email"
                  type="email"
                  placeholder="your@email.com"
                  value={registerEmail}
                  onChange={(e) => setRegisterEmail(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <Label htmlFor="register-password" className="text-white">Password</Label>
                <Input
                  id="register-password"
                  type="password"
                  placeholder="••••••••"
                  value={registerPassword}
                  onChange={(e) => setRegisterPassword(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="register-password-input"
                />
              </div>
              <div>
                <Label htmlFor="register-age" className="text-white">Age</Label>
                <Input
                  id="register-age"
                  type="number"
                  placeholder="21+"
                  min="21"
                  value={registerAge}
                  onChange={(e) => setRegisterAge(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="register-age-input"
                />
              </div>
              <div>
                <Label htmlFor="register-gender" className="text-white">Gender</Label>
                <Select value={registerGender} onValueChange={setRegisterGender}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="register-gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="male" className="text-white">Male</SelectItem>
                    <SelectItem value="female" className="text-white">Female</SelectItem>
                    <SelectItem value="other" className="text-white">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="register-dob" className="text-white">Date of Birth</Label>
                <Input
                  id="register-dob"
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="register-dob-input"
                />
              </div>

              {/* Emergency Contact Section */}
              <div className="border-t border-slate-700 pt-4 mt-2">
                <p className="text-sm font-semibold text-purple-400 mb-1">Emergency Contact <span className="text-red-400">*</span></p>
                <p className="text-xs text-slate-400 mb-3">All emergency contact fields are required</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="emergency-first-name" className="text-white text-sm">First Name</Label>
                      <Input
                        id="emergency-first-name"
                        type="text"
                        placeholder="First name"
                        value={emergencyFirstName}
                        onChange={(e) => setEmergencyFirstName(e.target.value)}
                        required
                        className="bg-slate-800 border-slate-700 text-white"
                        data-testid="emergency-first-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="emergency-last-name" className="text-white text-sm">Last Name</Label>
                      <Input
                        id="emergency-last-name"
                        type="text"
                        placeholder="Last name"
                        value={emergencyLastName}
                        onChange={(e) => setEmergencyLastName(e.target.value)}
                        required
                        className="bg-slate-800 border-slate-700 text-white"
                        data-testid="emergency-last-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="emergency-phone" className="text-white text-sm">Contact Phone</Label>
                    <Input
                      id="emergency-phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={emergencyPhone}
                      onChange={(e) => setEmergencyPhone(e.target.value)}
                      required
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="emergency-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="emergency-relationship" className="text-white text-sm">Relationship</Label>
                    <Select value={emergencyRelationship} onValueChange={setEmergencyRelationship}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="emergency-relationship-select">
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="parent" className="text-white">Parent</SelectItem>
                        <SelectItem value="spouse" className="text-white">Spouse / Partner</SelectItem>
                        <SelectItem value="sibling" className="text-white">Sibling</SelectItem>
                        <SelectItem value="friend" className="text-white">Friend</SelectItem>
                        <SelectItem value="roommate" className="text-white">Roommate</SelectItem>
                        <SelectItem value="other" className="text-white">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="emergency-email" className="text-white text-sm">Contact Email <span className="text-amber-400 text-xs">(for safety notifications)</span></Label>
                    <Input
                      id="emergency-email"
                      type="email"
                      placeholder="contact@email.com"
                      value={emergencyEmail}
                      onChange={(e) => setEmergencyEmail(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="emergency-email-input"
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-slate-400 hover:text-slate-300 flex items-center gap-2 justify-center mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </Card>
    </div>
  );
};

export default Auth;
